import { _t } from "app/i18n/i18n";
import { isMobile } from "lib/hooks/useIsMobile";
import { JAPIAutoCompleteLoader } from "pages/latestResearch/publications/JAPIAutoCompleteLoader";
import { addParam, push } from "pages/search/api/filterUrlMaker";
import { CompanySuggestions } from "./CompanySuggestions";
import { KeywordSuggestions } from "pages/search/searchBar/KeywordSuggestions";
import React from "react";
import { Col, ListGroup, Row, Container } from "react-bootstrap";
import "./autocomplete.css";

export const AutoSuggestions = ({ keyword, setKeyword, setIsSearching }) => {
  return (
    <JAPIAutoCompleteLoader
      keyword={keyword}
      setIsSearching={setIsSearching}
      render={(suggestions) => {
        const analysts = suggestions.filter((suggest) => suggest.CategoryName === "Analyst");
        const companies = suggestions.filter((suggest) => suggest.CategoryName === "CompanyWithTicker");
        const periodicals = suggestions.filter((suggest) => suggest.CategoryName === "Report Titles");
        const themes = suggestions.filter((suggest) => suggest.CategoryName === "Theme").sort(sortAscending);
        const regions = suggestions.filter((suggest) => suggest.CategoryName === "Region");
        const countries = suggestions.filter((suggest) => suggest.CategoryName === "Country");

        const countryRegionColNum =
          analysts?.length && companies?.length && periodicals?.length && themes?.length ? 1 : 2;

        return (
          <Container fluid className={`${isMobile() ? "pb-3 mb-4 mt-md-0 border-bottom shadow bg-white" : ""}`}>
            <Row className={`${isMobile() ? "justify-content-end" : "auto-suggestion-row"}`}>
              <Col
                xl="2"
                lg="2"
                md="12"
                sm="12"
                className={`${isMobile() ? "" : "bg-white pb-3 shadow-bottom shadow-left border-left border-bottom auto-suggestion-col"}`}
              >
                <ListGroup className="list-group-flush">
                  <KeywordSuggestions keyword={keyword} setKeyword={setKeyword} />
                </ListGroup>
              </Col>

              {/* Analysts */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  analysts.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Analysts")} />
                  {analysts.map((suggestion, key) => (
                    <Suggestion key={key} suggestion={suggestion} setKeyword={setKeyword} />
                  ))}
                </ListGroup>
              </Col>

              {/* Regions */}
              <Col
                xl={countryRegionColNum}
                lg="1"
                md="6"
                sm="12"
                className={`${regions.length ? (isMobile() ? "" : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col") : "d-none"}`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Regions")} />
                  <ListGroup className="list-group-flush d-block">
                    {regions.map((suggestion, key) => (
                      <Suggestion key={key} suggestion={suggestion} setKeyword={setKeyword} />
                    ))}
                  </ListGroup>
                </ListGroup>
              </Col>

              {/* Countries */}
              <Col
                xl={countryRegionColNum}
                lg="1"
                md="6"
                sm="12"
                className={`${countries.length ? (isMobile() ? "" : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col") : "d-none"}`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Countries")} />
                  <ListGroup className="list-group-flush d-block">
                    {countries.map((suggestion, key) => (
                      <Suggestion key={key} suggestion={suggestion} setKeyword={setKeyword} />
                    ))}
                  </ListGroup>
                </ListGroup>
              </Col>

              {/* Themes */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  themes.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Themes")} />
                  {themes.map((suggestion, key) => (
                    <Suggestion key={key} suggestion={suggestion} setKeyword={setKeyword} />
                  ))}
                </ListGroup>
              </Col>

              {/* Companies */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  companies.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <CompanySuggestions
                    companies={companies}
                    keyword={keyword}
                    setKeyword={setKeyword}
                    nCompanyColumns="1"
                  />
                </ListGroup>
              </Col>

              {/* Periodicals  */}
              <Col
                xl="2"
                lg="2"
                md="6"
                sm="12"
                className={`${
                  periodicals.length
                    ? isMobile()
                      ? ""
                      : "bg-white pb-1 border-bottom shadow-bottom  auto-suggestion-col"
                    : "d-none"
                }`}
              >
                <ListGroup className="list-group-flush">
                  <Header label={_t("Periodicals")} />
                  <ListGroup className="list-group-flush d-block" style={{ columnCount: 1 }}>
                    {periodicals.map((suggestion, key) => (
                      <Suggestion key={key} suggestion={suggestion} setKeyword={setKeyword} />
                    ))}
                  </ListGroup>
                </ListGroup>
              </Col>
            </Row>
          </Container>
        );
      }}
    />
  );
};

export const Suggestion = ({ suggestion, setKeyword }) => (
  <ListGroup.Item
    as="a"
    onMouseDown={() => onAutoCompleteClick(suggestion, setKeyword)} // onMouse = pre-blur
    className="cursor-pointer py-1 text-info d-block"
  >
    {suggestion.label || suggestion.CategoryValue}
  </ListGroup.Item>
);

const onAutoCompleteClick = (suggestion, setKeyword) => {
  const category = suggestion.CategoryName || "";
  const categoryWithLang = category + (suggestion.CategoryLang && suggestion.CategoryLang === "ja" ? "-JA" : "");
  setKeyword("");
  push(addParam(categoryWithLang, suggestion.CategoryValue));
};

const Header = ({ label }) => (
  <ListGroup.Item className="h4 font-weight-lighter pl-2 pb-2 pt-3 mb-0 ">{label}</ListGroup.Item>
);

const sortAscending = (a, b) => {
  if (a.CategoryValue < b.CategoryValue) return -1;
  if (a.CategoryValue > b.CategoryValue) return 1;
  return 0;
};
